import { useEffect, useMemo, useState } from "react";
import {
  X, Save, Search, Package, FileText, ChevronRight, Loader2,
  CalendarClock, Wrench, ClipboardList, CheckCircle2, AlertTriangle,
} from "lucide-react";
import api from "../../lib/api";
import { toast } from "../ui/sonner";
import DateTimePicker from "../DateTimePicker";
import DictTextarea from "../DictTextarea";

/**
 * AddEquipmentSheet — Iter 398 replacement for the previous inline
 * AddEquipmentModal. Full-screen sheet with two workflows:
 *
 *  • Manual — pick or free-text an inventory item, optionally link a
 *    Job Card to auto-fill the install date, set service interval.
 *
 *  • Import from a Sales Order / Invoice — pick an existing doc for
 *    this customer, review its line items (freight / labour filtered
 *    out), choose which ones become serviceable components, and bulk-
 *    create equipment rows. Uses the existing
 *    /sales-documents/{did}/equipment/preview + /apply endpoints
 *    (extended in the same iteration to accept sales_order too).
 *
 * Pricing is NEVER surfaced or carried across — techs are onboarding
 * physical assets, not accounting.
 */
export default function AddEquipmentSheet({
  sites,
  presetSiteId,
  customerId,
  onClose,
  onCreated,
}) {
  const [mode, setMode] = useState("manual"); // manual | import
  const [siteId, setSiteId] = useState(
    presetSiteId || (sites?.[0]?.id) || "",
  );

  return (
    <div
      className="fixed inset-0 z-[80] bg-black/70 flex flex-col overflow-hidden"
      style={{
        // Iter 398c — Dirk showed a mobile screenshot with the header
        // overlapping the iOS status bar clock. Respect the notch on
        // top and the home-indicator gutter at the bottom.
        paddingTop: "env(safe-area-inset-top)",
        paddingBottom: "env(safe-area-inset-bottom)",
      }}
      data-testid="equipment-sheet"
    >
      {/* Header */}
      <header className="shrink-0 flex items-center justify-between px-4 sm:px-6 py-3 border-b border-white/10 bg-[#0a0b0e]">
        <div className="flex items-center gap-2 min-w-0">
          <Wrench className="w-4 h-4 text-emerald-400 shrink-0" />
          <div className="min-w-0">
            <h2 className="font-display text-base sm:text-lg font-bold text-white leading-tight">
              Add equipment
            </h2>
            <p className="text-[10px] sm:text-[11px] text-[#a0aabf] leading-tight">
              Onboard equipment, cartridges &amp; membranes for this site.
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="hv-btn hv-btn-ghost text-xs"
          data-testid="equipment-sheet-close"
        >
          <X className="w-4 h-4" />
          <span className="hidden sm:inline">Close</span>
        </button>
      </header>

      {/* Mode tabs */}
      <div className="shrink-0 flex items-center gap-1 px-4 sm:px-6 pt-3 border-b border-white/10 bg-[#0a0b0e]">
        <TabBtn
          active={mode === "manual"}
          onClick={() => setMode("manual")}
          icon={Package}
          label="Manual add"
          testId="equipment-sheet-tab-manual"
        />
        <TabBtn
          active={mode === "import"}
          onClick={() => setMode("import")}
          icon={FileText}
          label="Import from sales doc"
          testId="equipment-sheet-tab-import"
        />
      </div>

      {/* Body */}
      <div className="flex-1 overflow-y-auto bg-[#0a0b0e] px-4 sm:px-6 py-4">
        {/* Common site picker (locked when preset) */}
        <div className="mb-4 max-w-3xl mx-auto">
          <label className="text-[10px] uppercase tracking-wider text-[#647087] font-bold">
            Site *
          </label>
          {presetSiteId ? (
            <div className="mt-1 px-3 py-2 rounded border border-emerald-500/20 bg-emerald-500/[0.05] text-sm text-emerald-200">
              {sites.find((s) => s.id === presetSiteId)?.name || "This site"}
              <span className="ml-2 text-[10px] text-emerald-300/70 uppercase tracking-wider">
                locked
              </span>
            </div>
          ) : (
            <select
              value={siteId}
              onChange={(e) => setSiteId(e.target.value)}
              className="hv-input w-full text-sm mt-1"
              required
              data-testid="equipment-sheet-site"
            >
              <option value="">— Pick a site —</option>
              {sites.map((s) => (
                <option key={s.id} value={s.id}>{s.name}</option>
              ))}
            </select>
          )}
        </div>

        {mode === "manual" ? (
          <ManualAddForm
            siteId={siteId}
            customerId={customerId}
            onSaved={onCreated}
          />
        ) : (
          <ImportFromSalesDoc
            siteId={siteId}
            customerId={customerId}
            onApplied={onCreated}
          />
        )}
      </div>
    </div>
  );
}

function TabBtn({ active, onClick, icon: Icon, label, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className={`inline-flex items-center gap-1.5 px-3 py-2 text-xs font-semibold border-b-2 transition-colors ${
        active
          ? "border-emerald-400 text-emerald-300"
          : "border-transparent text-[#a0aabf] hover:text-white"
      }`}
    >
      <Icon className="w-3.5 h-3.5" />
      {label}
    </button>
  );
}

/* ─────────────────────────── Manual add ─────────────────────────── */

function ManualAddForm({ siteId, customerId, onSaved }) {
  const [name, setName] = useState("");
  const [model, setModel] = useState("");
  const [serial, setSerial] = useState("");
  const [installDate, setInstallDate] = useState("");
  const [notes, setNotes] = useState("");
  const [quantity, setQuantity] = useState(1);
  const [unit, setUnit] = useState("pcs");
  const [inventoryId, setInventoryId] = useState(null);
  const [inventoryLabel, setInventoryLabel] = useState("");
  const [linkedJc, setLinkedJc] = useState(null);
  const [intervalDays, setIntervalDays] = useState("");
  const [nextDue, setNextDue] = useState("");
  const [saving, setSaving] = useState(false);

  const canSubmit = !!name.trim() && !!siteId && !!customerId;

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit) {
      toast.error("Name and site are required.");
      return;
    }
    setSaving(true);
    try {
      await api.post("/equipment", {
        customer_id: customerId,
        site_id: siteId,
        name: name.trim(),
        model: model.trim() || null,
        serial: serial.trim() || null,
        install_date: installDate || null,
        notes: notes.trim() || null,
        inventory_item_id: inventoryId || null,
        quantity: Number(quantity) || 1,
        unit: unit || "pcs",
        // Iter 398 — no recurring toggle. If an interval was provided
        // treat the equipment as service-tracked automatically.
        is_service_item: !!intervalDays,
        interval_days: intervalDays ? Number(intervalDays) : null,
        next_service_date: nextDue || null,
      });
      toast.success(`${name.trim()} added.`);
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not add equipment.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={submit} className="max-w-3xl mx-auto space-y-4">
      {/* Inventory picker */}
      <div className="rounded-md border border-white/10 bg-white/[0.02] p-3 space-y-2">
        <div className="flex items-center gap-2 text-xs">
          <Package className="w-3.5 h-3.5 text-emerald-300" />
          <span className="font-bold uppercase tracking-wider text-emerald-300">
            Inventory link (optional)
          </span>
        </div>
        <InventoryPicker
          value={inventoryLabel}
          onPick={(item) => {
            if (item) {
              setInventoryId(item.id);
              setInventoryLabel(item.name || "");
              if (!name.trim()) setName(item.name || "");
              if (item.unit) setUnit(item.unit);
              if (item.default_interval_days && !intervalDays) {
                setIntervalDays(String(item.default_interval_days));
              }
            } else {
              setInventoryId(null);
              setInventoryLabel("");
            }
          }}
        />
        <p className="text-[10px] text-[#647087]">
          Search cartridges, membranes, pumps… or leave blank to record free-text.
        </p>
      </div>

      {/* Basic details */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div className="sm:col-span-2">
          <label className="text-[10px] uppercase tracking-wider text-[#647087] font-bold">
            Name *
          </label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="hv-input w-full text-sm mt-1"
            placeholder="e.g. Booster pump 2.2 kW · Membrane BW30-400"
            required
            data-testid="equipment-sheet-name"
          />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-wider text-[#647087] font-bold">
            Model
          </label>
          <input
            type="text"
            value={model}
            onChange={(e) => setModel(e.target.value)}
            className="hv-input w-full text-sm mt-1"
            data-testid="equipment-sheet-model"
          />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-wider text-[#647087] font-bold">
            Serial no.
          </label>
          <input
            type="text"
            value={serial}
            onChange={(e) => setSerial(e.target.value)}
            className="hv-input w-full text-sm mt-1"
            data-testid="equipment-sheet-serial"
          />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-wider text-[#647087] font-bold">
            Quantity
          </label>
          <input
            type="number"
            step="1"
            min="1"
            value={quantity}
            onChange={(e) => setQuantity(e.target.value)}
            className="hv-input w-full text-sm mt-1"
            data-testid="equipment-sheet-qty"
          />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-wider text-[#647087] font-bold">
            Unit
          </label>
          <input
            type="text"
            value={unit}
            onChange={(e) => setUnit(e.target.value)}
            className="hv-input w-full text-sm mt-1"
            data-testid="equipment-sheet-unit"
          />
        </div>
      </div>

      {/* Job card link */}
      <div className="rounded-md border border-white/10 bg-white/[0.02] p-3 space-y-2">
        <div className="flex items-center gap-2 text-xs">
          <ClipboardList className="w-3.5 h-3.5 text-cyan-300" />
          <span className="font-bold uppercase tracking-wider text-cyan-300">
            Link to a Job Card (optional)
          </span>
        </div>
        <JobCardPicker
          customerId={customerId}
          siteId={siteId}
          value={linkedJc}
          onPick={(jc) => {
            setLinkedJc(jc);
            if (jc) {
              const anchor =
                jc.actual_start
                || jc.scheduled_start
                || jc.created_at
                || null;
              if (anchor) {
                // Normalise to yyyy-mm-dd for the date field.
                const iso = String(anchor).slice(0, 10);
                setInstallDate(iso);
                if (!notes.trim()) {
                  setNotes(`Installed under JC ${jc.short_id || jc.id}`);
                }
              }
            }
          }}
        />
        <div>
          <label className="text-[10px] uppercase tracking-wider text-[#647087] font-bold">
            Install date
          </label>
          <input
            type="date"
            value={installDate}
            onChange={(e) => setInstallDate(e.target.value)}
            className="hv-input w-full text-sm mt-1"
            data-testid="equipment-sheet-install-date"
          />
        </div>
      </div>

      {/* Service interval — no recurring toggle */}
      <div className="rounded-md border border-white/10 bg-white/[0.02] p-3 space-y-2">
        <div className="flex items-center gap-2 text-xs">
          <CalendarClock className="w-3.5 h-3.5 text-amber-300" />
          <span className="font-bold uppercase tracking-wider text-amber-300">
            Service interval (optional)
          </span>
        </div>
        <p className="text-[10px] text-[#647087]">
          If this item needs periodic servicing, set the interval below —
          the scheduler will pick it up automatically.
        </p>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="text-[10px] uppercase tracking-wider text-[#647087] font-bold">
              Interval (days)
            </label>
            <input
              type="number"
              min="1"
              step="1"
              value={intervalDays}
              onChange={(e) => setIntervalDays(e.target.value)}
              placeholder="e.g. 90"
              className="hv-input w-full text-sm mt-1"
              data-testid="equipment-sheet-interval"
            />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-[#647087] font-bold">
              Next service
            </label>
            <DateTimePicker
              value={nextDue}
              onChange={setNextDue}
              showTime={false}
              placeholder="Pick date"
              className="mt-1"
              testId="equipment-sheet-next-due"
            />
          </div>
        </div>
      </div>

      <div>
        <label className="text-[10px] uppercase tracking-wider text-[#647087] font-bold">
          Notes
        </label>
        <DictTextarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          rows={2}
          className="hv-input w-full text-sm mt-1 resize-none"
          placeholder="Optional install notes, location, etc."
          data-testid="equipment-sheet-notes"
        />
      </div>

      <div className="sticky bottom-0 -mx-4 sm:-mx-6 px-4 sm:px-6 py-3 border-t border-white/10 bg-[#0a0b0e] flex items-center justify-end gap-2">
        <button
          type="submit"
          disabled={saving || !canSubmit}
          className="hv-btn hv-btn-primary text-sm disabled:opacity-50"
          data-testid="equipment-sheet-submit"
        >
          {saving ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <Save className="w-4 h-4" />
          )}
          {saving ? "Saving…" : "Add equipment"}
        </button>
      </div>
    </form>
  );
}

/* ────────────────────── Inventory picker (combo) ────────────────────── */

function InventoryPicker({ value, onPick }) {
  const [q, setQ] = useState(value || "");
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => setQ(value || ""), [value]);

  useEffect(() => {
    if (!open) return undefined;
    let alive = true;
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const { data } = await api.get(
          `/inventory?limit=30&paginated=true${q ? `&q=${encodeURIComponent(q)}` : ""}`,
        );
        if (!alive) return;
        setItems(data?.items || []);
      } finally {
        if (alive) setLoading(false);
      }
    }, 220);
    return () => { alive = false; clearTimeout(t); };
  }, [q, open]);

  return (
    <div className="relative">
      <div className="relative">
        <Search className="w-3.5 h-3.5 text-[#647087] absolute left-2.5 top-2.5" />
        <input
          type="text"
          value={q}
          onFocus={() => setOpen(true)}
          onChange={(e) => { setQ(e.target.value); setOpen(true); onPick(null); }}
          placeholder="Search inventory…"
          className="hv-input w-full text-sm pl-8"
          data-testid="equipment-sheet-inventory-search"
        />
      </div>
      {open && (
        <div className="absolute z-10 mt-1 w-full rounded-md border border-white/10 bg-[#111318] shadow-xl max-h-64 overflow-y-auto hv-inv-dropdown">
          {loading && (
            <div className="px-3 py-2 text-xs text-[#647087]">
              <Loader2 className="w-3 h-3 animate-spin inline mr-1" /> Searching…
            </div>
          )}
          {!loading && items.length === 0 && (
            <button
              type="button"
              onClick={() => { setOpen(false); onPick(null); }}
              className="w-full text-left px-3 py-2 text-xs text-[#a0aabf] hover:bg-white/[0.03]"
            >
              No inventory match — use &quot;{q || "free-text"}&quot; as-is.
            </button>
          )}
          {items.map((it) => (
            <button
              key={it.id}
              type="button"
              onClick={() => { onPick(it); setOpen(false); }}
              className="w-full text-left px-3 py-2 text-sm hover:bg-emerald-500/10 border-t border-white/5 first:border-t-0"
              data-testid={`equipment-sheet-inventory-opt-${it.id}`}
            >
              <div className="font-medium text-white truncate">{it.name}</div>
              <div className="text-[10px] text-[#647087] truncate">
                {it.sku ? `SKU ${it.sku}` : ""}
                {it.category ? ` · ${it.category}` : ""}
                {it.manufacturer ? ` · ${it.manufacturer}` : ""}
              </div>
            </button>
          ))}
        </div>
      )}
      {open && (
        <button
          type="button"
          onClick={() => setOpen(false)}
          className="fixed inset-0 z-0 cursor-default"
          aria-hidden
        />
      )}
    </div>
  );
}

/* ──────────────────────── Job Card picker ──────────────────────── */

function JobCardPicker({ customerId, siteId, value, onPick }) {
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedId, setSelectedId] = useState(value?.id || "");

  useEffect(() => setSelectedId(value?.id || ""), [value]);

  useEffect(() => {
    if (!customerId) return undefined;
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get(
          `/jobcards?customer_id=${customerId}&limit=200`,
        );
        if (!alive) return;
        const rows = Array.isArray(data) ? data : [];
        const filtered = siteId
          ? rows.filter((jc) => !jc.site_id || jc.site_id === siteId)
          : rows;
        setOptions(filtered.slice(0, 200));
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [customerId, siteId]);

  return (
    <select
      value={selectedId}
      onChange={(e) => {
        const id = e.target.value;
        setSelectedId(id);
        const jc = options.find((o) => o.id === id) || null;
        onPick(jc);
      }}
      className="hv-input w-full text-sm"
      data-testid="equipment-sheet-jc-picker"
    >
      <option value="">— No job card link —</option>
      {loading && <option disabled>Loading…</option>}
      {options.map((jc) => (
        <option key={jc.id} value={jc.id}>
          {jc.short_id ? `${jc.short_id} · ` : ""}{jc.title || "(untitled)"}
          {jc.status ? ` — ${jc.status}` : ""}
        </option>
      ))}
    </select>
  );
}

/* ────────────────── Import from Sales Order/Invoice ────────────────── */

function ImportFromSalesDoc({ siteId, customerId, onApplied }) {
  const [docs, setDocs] = useState([]);
  const [docId, setDocId] = useState("");
  const [loadingDocs, setLoadingDocs] = useState(false);
  const [preview, setPreview] = useState(null);
  const [previewErr, setPreviewErr] = useState("");
  const [lines, setLines] = useState([]);
  const [applying, setApplying] = useState(false);

  useEffect(() => {
    if (!customerId) return undefined;
    let alive = true;
    (async () => {
      setLoadingDocs(true);
      try {
        // Iter 400.22 — equipment extraction is INVOICES ONLY (+ credit
        // notes). Sales orders are promises, not deliveries — techs
        // shouldn't be able to attach equipment to a site from an SO.
        const [inv, cn] = await Promise.all([
          api.get(`/sales-documents?customer_id=${customerId}&category=invoice`),
          api.get(`/sales-documents?customer_id=${customerId}&category=credit_note`),
        ]);
        if (!alive) return;
        const combined = [
          ...(inv.data || []),
          ...(cn.data || []),
        ].sort((a, b) => String(b.document_date || b.created_at || "")
          .localeCompare(String(a.document_date || a.created_at || "")));
        setDocs(combined);
      } finally {
        if (alive) setLoadingDocs(false);
      }
    })();
    return () => { alive = false; };
  }, [customerId]);

  useEffect(() => {
    if (!docId) { setPreview(null); setLines([]); return undefined; }
    let alive = true;
    (async () => {
      setPreviewErr("");
      try {
        const qs = siteId ? `?site_id=${encodeURIComponent(siteId)}` : "";
        const { data } = await api.get(
          `/sales-documents/${docId}/equipment/preview${qs}`,
        );
        if (!alive) return;
        setPreview(data);
        setLines((data.lines || []).map((ln) => ({
          ...ln,
          include: !!ln.default_include && !ln.existing_equipment_id,
          is_service_item: !!ln.existing_is_service_item,
          interval_days: ln.existing_interval_days || "",
        })));
      } catch (e) {
        if (alive) setPreviewErr(e?.response?.data?.detail || "Could not load lines.");
      }
    })();
    return () => { alive = false; };
  }, [docId, siteId]);

  const includedCount = useMemo(
    () => lines.filter((l) => l.include).length,
    [lines],
  );

  const apply = async () => {
    if (!siteId) {
      toast.error("Pick a site first.");
      return;
    }
    const picked = lines.filter((l) => l.include).map((l) => ({
      line_index: l.line_index,
      name: l.name,
      quantity: l.quantity,
      unit: l.unit,
      inventory_item_id: l.matched_inventory_id || null,
      is_service_item: !!l.is_service_item,
      interval_days: l.is_service_item && l.interval_days ? Number(l.interval_days) : null,
    }));
    if (!picked.length) {
      toast.error("Pick at least one line to import.");
      return;
    }
    setApplying(true);
    try {
      const { data } = await api.post(
        `/sales-documents/${docId}/equipment/apply`,
        { site_id: siteId, lines: picked },
      );
      toast.success(
        `Imported ${data.created_count} new · ${data.updated_count} updated.`,
      );
      onApplied();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Import failed.");
    } finally {
      setApplying(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto space-y-4">
      <div className="rounded-md border border-white/10 bg-white/[0.02] p-3 space-y-2">
        <label className="text-[10px] uppercase tracking-wider text-[#647087] font-bold">
          Pick a Sales Order or Invoice
        </label>
        <select
          value={docId}
          onChange={(e) => setDocId(e.target.value)}
          className="hv-input w-full text-sm"
          disabled={loadingDocs}
          data-testid="equipment-sheet-doc-picker"
        >
          <option value="">
            {loadingDocs ? "Loading…" : "— Pick a document —"}
          </option>
          {docs.map((d) => (
            <option key={d.id} value={d.id}>
              [{(d.category || "").replace("_", " ")}]{" "}
              {d.document_number || d.title || d.id?.slice(0, 8)}
              {d.document_date ? ` · ${String(d.document_date).slice(0, 10)}` : ""}
              {d.audience === "internal" ? " · DNU" : ""}
            </option>
          ))}
        </select>
        <p className="text-[10px] text-[#647087]">
          Pricing is stripped on import — only names, quantities, units,
          and inventory links are copied to the site equipment list.
        </p>
      </div>

      {previewErr && (
        <div className="rounded-md border border-rose-500/40 bg-rose-500/10 p-3 text-sm text-rose-200 flex items-center gap-2">
          <AlertTriangle className="w-4 h-4" /> {previewErr}
        </div>
      )}

      {preview && (
        <div className="rounded-md border border-white/10 bg-white/[0.02] overflow-hidden">
          <div className="px-3 py-2 border-b border-white/10 flex items-center justify-between text-xs">
            <div className="text-[#a0aabf]">
              <span className="font-semibold text-white">
                {preview.doc?.document_number || preview.doc?.title || "Document"}
              </span>
              {" · "}
              {lines.length} line{lines.length === 1 ? "" : "s"}
              {" · "}
              {includedCount} selected
            </div>
            {preview.existing_count_on_doc > 0 && (
              <span className="inline-flex items-center gap-1 text-emerald-300">
                <CheckCircle2 className="w-3 h-3" />
                {preview.existing_count_on_doc} already imported
              </span>
            )}
          </div>
          <div className="divide-y divide-white/5 max-h-[50vh] overflow-y-auto">
            {lines.length === 0 && (
              <div className="px-3 py-6 text-center text-xs text-[#647087]">
                This document has no line items.
              </div>
            )}
            {lines.map((ln, i) => (
              <div
                key={ln.line_index}
                className={`px-3 py-2 text-sm ${ln.existing_equipment_id ? "bg-emerald-500/[0.04]" : ""}`}
                data-testid={`equipment-sheet-import-line-${i}`}
              >
                <div className="flex items-start gap-3">
                  <input
                    type="checkbox"
                    checked={ln.include}
                    onChange={(e) => setLines((prev) => prev.map(
                      (r, idx) => idx === i ? { ...r, include: e.target.checked } : r,
                    ))}
                    className="mt-1 w-4 h-4 shrink-0 accent-emerald-400"
                    data-testid={`equipment-sheet-import-check-${i}`}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-white truncate">{ln.name}</span>
                      {ln.is_likely_non_equipment && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded border border-amber-500/40 text-amber-300 bg-amber-500/10">
                          likely non-equipment
                        </span>
                      )}
                      {ln.existing_equipment_id && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded border border-emerald-500/40 text-emerald-300 bg-emerald-500/10">
                          already imported
                        </span>
                      )}
                    </div>
                    <div className="text-[10px] text-[#647087] mt-0.5">
                      {ln.quantity} {ln.unit}
                      {ln.matched_inventory_id ? ` · inventory link ✓` : ""}
                      {ln.sku ? ` · SKU ${ln.sku}` : ""}
                    </div>
                  </div>
                </div>
                {ln.include && (
                  <div className="mt-2 pl-7 grid grid-cols-2 gap-2 max-w-md">
                    <label className="flex items-center gap-1.5 text-xs text-[#dde3ec]">
                      <input
                        type="checkbox"
                        checked={ln.is_service_item}
                        onChange={(e) => setLines((prev) => prev.map(
                          (r, idx) => idx === i ? { ...r, is_service_item: e.target.checked } : r,
                        ))}
                        className="w-3.5 h-3.5 accent-amber-400"
                        data-testid={`equipment-sheet-import-service-${i}`}
                      />
                      Serviceable
                    </label>
                    {ln.is_service_item && (
                      <input
                        type="number"
                        min="1"
                        step="1"
                        value={ln.interval_days}
                        onChange={(e) => setLines((prev) => prev.map(
                          (r, idx) => idx === i ? { ...r, interval_days: e.target.value } : r,
                        ))}
                        placeholder="days"
                        className="hv-input text-xs"
                        data-testid={`equipment-sheet-import-interval-${i}`}
                      />
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
          <div className="px-3 py-3 border-t border-white/10 flex items-center justify-between bg-[#0a0b0e]">
            <div className="text-xs text-[#647087]">
              {includedCount} of {lines.length} selected
            </div>
            <button
              type="button"
              onClick={apply}
              disabled={applying || includedCount === 0 || !siteId}
              className="hv-btn hv-btn-primary text-sm disabled:opacity-50"
              data-testid="equipment-sheet-import-apply"
            >
              {applying ? <Loader2 className="w-4 h-4 animate-spin" /> : <ChevronRight className="w-4 h-4" />}
              {applying ? "Importing…" : `Import ${includedCount} to site`}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
